import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { site } from '../data/site';

type SEOProps = {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  keywords?: string[];
  structuredData?: Record<string, unknown>;
};

function setMeta(selector: string, attrs: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
    return;
  }
  Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
}

export function SEO({
  title,
  description = site.description,
  canonical,
  image = `${site.url}/social-card.svg`,
  type = 'website',
  keywords = site.keywords,
  structuredData
}: SEOProps) {
  const location = useLocation();
  const url = canonical ?? `${site.url}${location.pathname}`;
  const fullTitle = title.includes(site.name) ? title : `${title} | ${site.name}`;

  useEffect(() => {
    document.title = fullTitle;
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="keywords"]', { name: 'keywords', content: keywords.join(', ') });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = url;

    const oldJsonLd = document.getElementById('structured-data');
    oldJsonLd?.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'structured-data';
    script.text = JSON.stringify(
      structuredData ?? {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: site.name,
        url: site.url,
        image,
        areaServed: 'Massachusetts',
        description,
        knowsAbout: site.keywords,
        sameAs: []
      }
    );
    document.head.appendChild(script);
  }, [canonical, description, fullTitle, image, keywords, structuredData, type, url]);

  return null;
}
